// src/pages/AIGolfPage.jsx
import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import QrCode2TwoToneIcon from "@mui/icons-material/QrCode2TwoTone";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { ReceiptLongTwoTone, AccountCircle } from "@mui/icons-material";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";

import { AuthContext } from "../context/AuthContext";
import api from "../api/api";

import LoginDialog from "../component/LoginDialog";
import FadeWrapper from "../component/FadeWrapper";
import QRCodeDialog from "../component/QRCodeDialog";
import ImageGallery from "../component/ImageGallery";
import AboutCollage from "../component/QuadCollage";
import { StoreInfoBanner } from "../component/StoreInfoBanner";
// import verifyToken from "../api/user/verifyToken"; // unused
import getUser from "../api/app/getUser";
import getOrders from "../api/app/getOrders";
import getSimulatorReservations from "../api/app/getSimulatorReservations";
// import { ORDER_DEMO } from "../data/ORDER_DEMO";

function decodeJwtPayload(token) {
  try {
    const part = token?.split?.(".")?.[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(b64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function tokenSecondsLeft(token) {
  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;
  if (!exp) return null;
  const now = Math.floor(Date.now() / 1000);
  return exp - now;
}

function fmtSec(sec) {
  if (sec == null) return "";
  const s = Math.max(0, sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}h ${m}m ${r}s`;
  if (m > 0) return `${m}m ${r}s`;
  return `${r}s`;
}

const AIGolfPage = () => {
  const navigate = useNavigate();
  const { storeId } = useParams();
  const theme = useTheme();

  const { token, setAuth, clearAuth } = useContext(AuthContext);

  const [loginOpen, setLoginOpen] = useState(false);
  const [openQRCode, setOpenQRCode] = useState(false);
  const [qrStrings, setQrStrings] = useState([]);

  const [userData, setUserData] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  // store info states
  const [storeInfo, setStoreInfo] = useState(null);
  const [storeInfoLoading, setStoreInfoLoading] = useState(true);
  const [storeInfoError, setStoreInfoError] = useState(null);

  // auth probe UI (no backend changes required)
  const [authProbe, setAuthProbe] = useState({
    checking: false,
    ok: null, // true/false/null
    status: null, // 200/401/403/...
    reason: "",
    at: null,
  });

  // keep a countdown for token exp display
  const [tokenLeftSec, setTokenLeftSec] = useState(() =>
    tokenSecondsLeft(token)
  );

  useEffect(() => {
    if (!token) {
      setTokenLeftSec(null);
      return;
    }
    setTokenLeftSec(tokenSecondsLeft(token));
    const t = setInterval(() => {
      setTokenLeftSec(tokenSecondsLeft(token));
    }, 1000);
    return () => clearInterval(t);
  }, [token]);

  // ------- validate token by calling a protected API (probe) -------
  // Changes:
  // 1) Do NOT auto-logout on 403 (token can still be valid; it's a permission issue)
  // 2) Remove the "message.includes('401')" heuristic to avoid false logouts
  // 3) Record probe status in UI so you can confirm auth behavior
  useEffect(() => {
    if (!token || !storeId) {
      setAuthProbe({
        checking: false,
        ok: null,
        status: null,
        reason: token ? "缺少店家資訊" : "尚未登入",
        at: Date.now(),
      });
      return;
    }

    let alive = true;

    const probe = async () => {
      try {
        setAuthProbe({
          checking: true,
          ok: null,
          status: null,
          reason: "驗證中...",
          at: Date.now(),
        });

        await getSimulatorReservations(storeId, new Date().toISOString());

        if (!alive) return;
        setAuthProbe({
          checking: false,
          ok: true,
          status: 200,
          reason: "驗證成功",
          at: Date.now(),
        });
      } catch (e) {
        const status = e?.response?.status ?? e?.status ?? null;
        if (!alive) return;

        if (status === 401) {
          setAuthProbe({
            checking: false,
            ok: false,
            status: 401,
            reason: "登入已過期，請重新登入",
            at: Date.now(),
          });
          clearAuth();
          setUserData(null);
          setLoginOpen(true);
          return;
        }

        if (status === 403) {
          // token likely valid; this endpoint is forbidden due to ACL/role/store
          setAuthProbe({
            checking: false,
            ok: true,
            status: 403,
            reason: "驗證成功，但無此功能權限",
            at: Date.now(),
          });
          return;
        }

        setAuthProbe({
          checking: false,
          ok: null,
          status,
          reason: `連線異常 (${status ?? "未知"})`,
          at: Date.now(),
        });
        console.error("auth probe error:", e);
      }
    };

    probe();

    return () => {
      alive = false;
    };
  }, [token, storeId, clearAuth]);

  // ------- fetch store_info.json -------
  useEffect(() => {
    if (!storeId) return;
    const url = `${process.env.REACT_APP_API_ENDPOINT}/media/${storeId}/store_info.json`;

    let alive = true;
    (async () => {
      try {
        setStoreInfoLoading(true);
        setStoreInfoError(null);
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const normalized = {
          name: data.name ?? "AI GOLF",
          businessHours: data.businessHours ?? data.business_hours ?? "",
          address: data.address ?? "",
          email: data.email ?? "",
          phone: data.phone ?? "",
          instagram: data.instagram ?? "",
          facebook: data.facebook ?? "",
          lineOfficial: data.lineOfficial ?? data.line_official ?? "",
          threads: data.threads ?? "",
          note: data.note ?? "",
        };
        if (alive) setStoreInfo(normalized);
      } catch (err) {
        if (alive) setStoreInfoError(String(err));
      } finally {
        if (alive) setStoreInfoLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [storeId]);

  // ------- fetch user only when token is ready -------
  useEffect(() => {
    if (!token) {
      setUserData(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        setUserLoading(true);
        const me = await getUser();
        if (alive) setUserData(me);
      } catch (e) {
        if (alive) setUserData(null);
      } finally {
        if (alive) setUserLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  // ------- fetch orders only when token exists -------
  useEffect(() => {
    console.log("[AIGolfPage] Orders useEffect triggered, token:", token ? "exists" : "null");
    if (!token) return;

    const getCurrentOrderIds = (data) => {
      const eightHoursMs = 8 * 60 * 60 * 1000; // 8 hours in milliseconds
      const now = new Date(Date.now() + eightHoursMs); // Adjust for backend timezone format error
      const oneHourMs = 60 * 60 * 1000; // 1 hour in milliseconds

      return data
        .filter((item) => {
          const start = new Date(item.startTime);
          const end = new Date(item.endTime);

          // Show QR code 1 hour before start time
          return now >= start.getTime() - oneHourMs && now <= end;
        })
        .map((item) => ({
          id: item.id,
          name: item.storeSimulator?.name || item.id,
        }));
    };

    (async () => {
      try {
        const res = await getOrders();
        console.log("[AIGolfPage] getOrders response:", res);
        const orders = res ? getCurrentOrderIds(res.data) : [];
        console.log("[AIGolfPage] getCurrentOrderIds:", orders);
        setQrStrings(orders);
      } catch (err) {
        console.error("[AIGolfPage] getOrders error:", err);
        setQrStrings([]);
      }
    })();
  }, [token]);

  // ------- actions -------
  const handleLoginOpen = () => setLoginOpen(true);

  const logout = () => {
    clearAuth();
    setUserData(null);
  };

  const goReserve = () => {
    if (!token) {
      setLoginOpen(true);
      return;
    }
    navigate(`/app/${storeId}/rooms`);
  };

  const goCharge = async () => {
    if (!token) {
      setLoginOpen(true);
      return;
    }
    if (!userData?.id) {
      try {
        setUserLoading(true);
        const me = await getUser();
        setUserData(me);
        if (!me?.id) {
          setLoginOpen(true);
          return;
        }
      } catch {
        setLoginOpen(true);
        return;
      } finally {
        setUserLoading(false);
      }
    }
    navigate(`/app/${storeId}/${userData.id}/charge`);
  };

  const authLabel = !token
    ? "尚未登入"
    : authProbe.checking
      ? "驗證中"
      : authProbe.status
        ? `狀態: ${authProbe.status}`
        : "狀態: -";

  return (
    <FadeWrapper>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          flexDirection: "column",
          minHeight: "100vh",
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* Top AppBar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: theme.palette.background.paper,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1,
            }}
          >
            {/* Brand */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                color="info.main"
                sx={{ display: "flex", alignItems: "baseline", m: -1 }}
              >
                <Typography
                  component="span"
                  variant="h5"
                  sx={{ fontWeight: 800, mr: 1 }}
                >
                  AIGOLF
                </Typography>
                <Typography
                  component="span"
                  variant="body1"
                  sx={{ fontWeight: 500, color: "text.secondary" }}
                >
                  室內高爾夫運動會館
                </Typography>
              </Typography>
            </Box>

            {/* Auth buttons + probe display */}
            <Box sx={{ display: "flex", alignItems: "center", pt: 1, gap: 1 }}>


              {token ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: "info.main",
                      color: "info.main",
                      fontWeight: "bold",
                      borderRadius: 10,
                      px: 1,
                      "&:hover": {
                        backgroundColor: "info.main",
                        color: "white",
                      },
                    }}
                    onClick={logout}
                  >
                    登出
                  </Button>
                </>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: "info.main",
                    color: "info.main",
                    fontWeight: "bold",
                    borderRadius: 10,
                    px: 1,
                    "&:hover": {
                      backgroundColor: "info.main",
                      color: "white",
                    },
                  }}
                  onClick={handleLoginOpen}
                >
                  登入 / 註冊
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        {/* Hero gallery */}
        <ImageGallery storeId={storeId} />
        <Box sx={{ width: "100%", my: 5 }} />

        {/* Centered action buttons (only when logged in) */}
        {token && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            mt={1}
            px={2}
          >
            {[
              {
                label: "入場碼",
                icon: <QrCode2TwoToneIcon />,
                onClick: () => setOpenQRCode((o) => !o),
              },
              { label: "儲值", icon: <PaidRoundedIcon />, onClick: goCharge },
              {
                label: "個人資料",
                icon: <AccountCircle />,
                onClick: () => navigate("/app/profile"),
              },
              {
                label: "查訂單",
                icon: <ReceiptLongTwoTone />,
                onClick: () => navigate("/app/myOrder"),
              },
            ].map(({ label, icon, onClick }, i) => (
              <IconButton
                key={i}
                onClick={onClick}
                disabled={userLoading && label === "儲值"}
                sx={(theme) => ({
                  width: "120px",
                  px: 1.5,
                  py: 0.5,
                  border: `2px solid ${theme.palette.info.main}`,
                  borderRadius: "12px",
                  color: theme.palette.info.main,
                  display: "flex",
                  justifyContent: "center", // Center content
                  alignItems: "center",
                  gap: 0.5,
                  "&:hover": {
                    backgroundColor: theme.palette.info.main,
                    color: "#fff",
                  },
                })}
              >
                {icon}
                <Box
                  component="span"
                  sx={{ fontSize: "0.9rem", fontWeight: 600, whiteSpace: "nowrap" }}
                >
                  {label}
                </Box>
              </IconButton>
            ))}
          </Box>
        )}

        <Box sx={{ width: "100%", my: 5 }} />

        {/* Main centered content container */}
        <Box
          sx={{
            maxWidth: 960,
            mx: "auto",
            px: { xs: 2, sm: 4 },
          }}
        >
          {/* About */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
          >
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              sx={{
                fontWeight: 700,
                width: "100%",
                color: theme.palette.text.primary,
              }}
            >
              About AIGolf
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ fontSize: "18px", lineHeight: 1.8 }}
            >
              AI GOLF秉持著「平價也能享受高品質」的理念，想體驗高爾夫的樂趣，不一定要高價門票。
              平價也能享受專業又舒適的擊球環境。不論艷陽天或下雨天，皆提供舒適的室內擊球與休閒環境，
              打造適合多人同樂的空間，讓 GOLF 不只是個人運動，而是與朋友、家人共聚的時光。
            </Typography>

            <Box sx={{ width: "100%", my: 1 }} />
            <AboutCollage
              storeId={storeId}
              variant="magzine"
              ratio="16/9"
              captions={["", "", "", ""]}
            />
          </Box>

          <Box sx={{ width: "100%", my: 5 }} />

          {/* Features */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            width="100%"
          >
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              sx={{
                fontWeight: 700,
                width: "100%",
                color: theme.palette.text.primary,
              }}
            >
              Our Features Advantages
            </Typography>
            <Box
              component="img"
              src={`${process.env.REACT_APP_API_ENDPOINT}/media/${storeId}/feature/banner`}
              alt="會員方案"
              sx={{
                width: "120%",
                maxWidth: "120%",
                height: "auto",
                marginLeft: "-10%",
              }}
            />
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ fontSize: "18px", lineHeight: 1.8, mt: 2 }}
            >
              AI GOLF 系統採用韓國 Bravo Golf，擁有世界 176 個球場。雷射精準，體驗流暢。
              新手老手都能輕鬆上手。
            </Typography>

            <Box sx={{ width: "100%", my: 1 }} />
            <AboutCollage
              storeId={storeId}
              variant="magzine"
              ratio="16/9"
              folder="feature"
              captions={["", "", "", ""]}
            />
            <Box sx={{ width: "100%", my: 5 }} />
          </Box>

          {/* Pricing */}
          <Box sx={{ width: "100%", my: 1 }} />
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            sx={{
              fontWeight: 700,
              width: "100%",
              color: theme.palette.text.primary,
            }}
          >
            Pricing Plan
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              my: 2,
            }}
          >
            <Box
              component="img"
              src={`${process.env.REACT_APP_API_ENDPOINT}/media/${storeId}/member_plan/member_plan.png`}
              alt="會員方案"
              sx={{
                width: "90%",
                maxWidth: 800,
                borderRadius: 2,
                boxShadow: `0 4px 20px ${theme.palette.info.main}33`,
                border: `1px solid ${theme.palette.divider}`,
                objectFit: "contain",
              }}
            />
          </Box>

          <StoreInfoBanner
            info={storeInfo}
            storeId={storeId}
            loading={storeInfoLoading}
            error={storeInfoError}
            theme={theme}
          />
        </Box>
      </Box>

      <LoginDialog
        openLogin={loginOpen}
        handleLoginClose={() => setLoginOpen(false)}
        setToken={(t) => {
          // unified token handling: remember token, no need to re-login
          if (t) {
            setAuth(t);
          }
        }}
      />

      <QRCodeDialog
        open={openQRCode}
        onClose={() => setOpenQRCode(false)}
        qrStrings={qrStrings}
        infoString={token ? "入場碼" : "尚未登入"}
      />

      {/* Floating CTA */}
      <Box
        sx={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: { xs: 12, sm: 16 },
          zIndex: 1200,
          width: "min(640px, 92vw)",
          px: 2,
          pb: "env(safe-area-inset-bottom)",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          size="large"
          startIcon={<CalendarMonthIcon />}
          onClick={goReserve}
          sx={(theme) => ({
            py: 1.2,
            fontWeight: 800,
            letterSpacing: 0.5,
            borderRadius: 999,
            backgroundColor: theme.palette.info.main,
            color: theme.palette.text.selected,
            boxShadow: `0 8px 24px ${theme.palette.info.main}55`,
            textTransform: "none",
            "&:hover": {
              backgroundColor: theme.palette.info.dark ?? theme.palette.info.main,
            },
          })}
        >
          立即預約
        </Button>
      </Box>
    </FadeWrapper>
  );
};

export default AIGolfPage;
